<template>
  <div class="ridership-by-stop-chart">
    <div v-if="!hasData" class="text-center text-muted py-5">
      {{ $t('flexigo.ridership.noData') }}
    </div>
    <template v-else>
      <highcharts ref="chart" :options="chartOptions" />
      <div v-if="categories.length > LIMIT" class="view-more-row">
        <button type="button" class="view-more-btn" @click="expanded = !expanded">
          {{
            expanded
              ? $t('flexigo.ridership.viewLess')
              : $t('flexigo.ridership.viewMore', { count: categories.length - LIMIT })
          }}
          <span class="view-more-arrow">{{ expanded ? '&#8593;' : '&#8595;' }}</span>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import Highcharts from 'highcharts';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import HighchartsPatternFill from 'highcharts/modules/pattern-fill';
import { Chart } from 'highcharts-vue';
import { format } from 'date-fns';
import { getBrowserLocaleFormat } from '@/helpers/dateFormatter';

HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);
HighchartsPatternFill(Highcharts);

export default {
  name: 'RidershipByStopChart',
  components: {
    highcharts: Chart,
  },
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    series: {
      type: Array,
      default: () => [],
    },
    metric: {
      type: String,
      default: 'sum',
    },
    dateRange: {
      type: Object,
      default: null,
    },
    routeColorMap: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      expanded: false,
      LIMIT: 10,
    };
  },
  computed: {
    hasData() {
      return this.categories.length > 0 && this.series.length > 0;
    },
    displayedCategories() {
      return this.expanded ? this.categories : this.categories.slice(0, this.LIMIT);
    },
    displayedSeries() {
      return this.series.map((s) => ({
        ...s,
        data: this.expanded ? s.data : s.data.slice(0, this.LIMIT),
      }));
    },
    dynamicHeight() {
      return Math.max(300, this.displayedCategories.length * 50);
    },
    /**
     * Friendly date/period label, mirroring RidershipByRouteChart's logic so both
     * tooltips read the same. Localized preset name when available; falls back to
     * a browser-locale date range; empty when dateRange is unknown.
     */
    periodLabel() {
      const range = this.dateRange;
      if (!range) return '';
      const PRESET_KEYS = {
        today: 'flexigo.date.today',
        yesterday: 'flexigo.date.yesterday',
        thisWeek: 'flexigo.date.thisWeek',
        lastSevenDays: 'flexigo.date.lastSevenDays',
        thisMonth: 'flexigo.date.thisMonth',
        lastThirtyDays: 'flexigo.date.lastThirtyDays',
        lastMonth: 'flexigo.date.lastMonth',
      };
      if (range.type && PRESET_KEYS[range.type]) {
        return this.$t(PRESET_KEYS[range.type]);
      }
      const fmt = (d) => {
        if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
        return format(d, getBrowserLocaleFormat({ type: 'date' }));
      };
      const startStr = fmt(range.startDate);
      const endStr = fmt(range.endDate);
      if (startStr && endStr) {
        return startStr === endStr ? startStr : `${startStr}\u2013${endStr}`;
      }
      return '';
    },
    chartOptions() {
      const { metric, periodLabel, routeColorMap } = this;
      const t = this.$t.bind(this);
      // Pre-resolve localized labels so they're available inside the (non-Vue-bound) Highcharts formatter.
      const labels = {
        on: t('flexigo.ridership.on'),
        off: t('flexigo.ridership.off'),
        total: t('flexigo.ridership.total'),
        route: t('flexigo.ridership.route'),
      };

      return {
        chart: {
          type: 'bar',
          height: this.dynamicHeight,
          style: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
        },
        credits: { enabled: false },
        // Native Highcharts export menu is disabled — a custom ExportMenu
        // dropdown (PNG + Excel) lives in the card header, matching the OTP
        // dashboard pattern.
        exporting: { enabled: false },
        title: { text: null },
        xAxis: {
          categories: this.displayedCategories,
          title: { text: null },
          lineColor: '#e8ecf1',
          tickColor: '#e8ecf1',
          labels: { style: { fontSize: '12px', color: '#666' } },
          gridLineWidth: 0,
        },
        yAxis: {
          min: 0,
          // reversedStacks defaults to true, which places the first series at
          // the end of the stack. Set false so On (first series) renders on the
          // left and Off (second series) on the right — matching the legend order.
          reversedStacks: false,
          title: {
            text: 'Ridership',
            style: { color: '#8896ab', fontSize: '12px' },
            margin: 35,
          },
          gridLineColor: '#f0f0f0',
          stackLabels: { enabled: false },
        },
        legend: { enabled: false },
        tooltip: {
          shared: true,
          useHTML: true,
          backgroundColor: '#2d3548',
          borderColor: '#2d3548',
          borderRadius: 10,
          shadow: { color: 'rgba(0,0,0,0.2)', offsetX: 0, offsetY: 4, opacity: 1, width: 12 },
          style: { color: '#fff', fontSize: '13px', maxHeight: '300px', overflowY: 'auto' },
          formatter() {
            // Spec lines 108–113: tooltip must show stop name, period, and per-route
            // breakdown (Sum: route | On | Off; On/Off: total + route | <metric>).
            // Per-bar `routeBreakdown` is injected upstream by stopChartData.
            const stopName = this.x;
            const firstPoint = this.points && this.points[0];
            const breakdown = firstPoint?.point?.options?.routeBreakdown ||
              firstPoint?.point?.routeBreakdown || { totalOn: 0, totalOff: 0, routes: [] };
            const { totalOn = 0, totalOff = 0, routes = [] } = breakdown;

            let html = `<div style="font-weight:600;margin-bottom:2px">${stopName}</div>`;
            if (periodLabel) {
              html += `<div style="color:#cbd5e0;font-size:12px;margin-bottom:4px">${periodLabel}</div>`;
            }
            html += '<div style="border-top:1px solid #4a5568;margin:6px 0"></div>';

            const swatch = (name) => {
              const color = routeColorMap[name] || '#8896ab';
              return `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${color};margin-right:6px;flex:none"></span>`;
            };

            if (metric === 'sum') {
              // Header row + per-route On/Off + Total row.
              html += `<div style="display:flex;justify-content:space-between;gap:8px;color:#cbd5e0;font-size:12px"><span>${labels.route}</span><span style="display:flex;gap:16px"><span style="width:40px;text-align:right;flex-shrink:0">${labels.on}</span><span style="width:40px;text-align:right;flex-shrink:0">${labels.off}</span></span></div>`;
              html += '<div style="border-top:1px solid #4a5568;margin:6px 0"></div>';
              const sortedRoutes = [...routes]
                .filter((r) => (r.totalOn || 0) + (r.totalOff || 0) > 0)
                .sort((a, b) => b.totalOn + b.totalOff - (a.totalOn + a.totalOff));
              sortedRoutes.forEach((r) => {
                html += `<div style="display:flex;justify-content:space-between;gap:8px;align-items:center"><span style="display:flex;align-items:center;color:#cbd5e0">${swatch(
                  r.routeName,
                )}${
                  r.routeName
                }</span><span style="display:flex;gap:16px"><span style="width:40px;text-align:right;flex-shrink:0;font-weight:600">${
                  r.totalOn
                }</span><span style="width:40px;text-align:right;flex-shrink:0;font-weight:600">${
                  r.totalOff
                }</span></span></div>`;
              });
              html += '<div style="border-top:1px solid #4a5568;margin:6px 0"></div>';
              html += `<div style="display:flex;justify-content:space-between;gap:8px"><span style="color:#fff;font-weight:700">${labels.total}</span><span style="display:flex;gap:16px"><span style="width:40px;text-align:right;flex-shrink:0;font-weight:700;color:#fff">${totalOn}</span><span style="width:40px;text-align:right;flex-shrink:0;font-weight:700;color:#fff">${totalOff}</span></span></div>`;
            } else {
              // 'on' or 'off' — show Total <metric> row, then per-route breakdown for that metric only.
              const isOn = metric === 'on';
              const metricLabel = isOn ? labels.on : labels.off;
              const total = isOn ? totalOn : totalOff;
              html += `<div style="display:flex;justify-content:space-between;gap:24px;margin-bottom:6px"><span style="color:#cbd5e0">${labels.total} ${metricLabel}</span><span style="font-weight:600">${total}</span></div>`;
              html += '<div style="border-top:1px solid #4a5568;margin:6px 0"></div>';
              const sortedRoutes = [...routes]
                .filter((r) => (isOn ? r.totalOn : r.totalOff) > 0)
                .sort((a, b) => (isOn ? b.totalOn - a.totalOn : b.totalOff - a.totalOff));
              sortedRoutes.forEach((r) => {
                const value = isOn ? r.totalOn : r.totalOff;
                html += `<div style="display:flex;justify-content:space-between;gap:8px;align-items:center"><span style="display:flex;align-items:center;color:#cbd5e0">${swatch(
                  r.routeName,
                )}${r.routeName}</span><span style="font-weight:600">${value}</span></div>`;
              });
            }
            return html;
          },
        },
        plotOptions: {
          bar: {
            stacking: 'normal',
            borderWidth: 0,
            borderRadius: 0,
            pointPadding: 0.1,
            groupPadding: 0.1,
            dataLabels: { enabled: false },
          },
        },
        series: this.displayedSeries,
      };
    },
  },
  watch: {
    categories() {
      this.expanded = false;
    },
  },
};
</script>

<style scoped>
.ridership-by-stop-chart {
  width: 100%;
  min-height: 400px;
}
.view-more-row {
  display: flex;
  justify-content: center;
  padding: 8px 0 4px;
  border-top: 1px solid #e8ecf1;
  margin-top: 4px;
}
.view-more-btn {
  background: none;
  border: none;
  color: #4a6cf7;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.15s;
}
.view-more-btn:hover {
  background: #f0f3ff;
}
.view-more-arrow {
  font-size: 11px;
}
</style>
